import { useCallback, useState } from "react";
import { observer } from "mobx-react-lite";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SignOutEscapeButton } from "@/components/auth/SignOutEscapeButton";
import { FRANKLIN_PRIVACY_URL, FRANKLIN_TERMS_URL } from "@/components/auth/LoginBrandPanel";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useUserStore } from "@/hooks/useUserStore";

interface TermsOfServiceModalProps {
  /** `auth.users.id` — the key `acceptTos` writes `tos_accepted_at` by. */
  userId: string;
  /** Fires after acceptance is persisted, so a host flow (e.g. signup) can advance. */
  onAccepted?: () => void;
}

const LINK_CLASS = "font-medium underline underline-offset-2 hover:text-primary";

/**
 * Blocking consent gate: every account with `user_profiles.tos_accepted_at` NULL
 * must accept the Terms of Service and the Privacy Policy before using the app.
 * Self-contained on purpose — it only needs a `userId`, so the signup flow can
 * mount it as-is later.
 */
export const TermsOfServiceModal = observer(function TermsOfServiceModal({
  userId,
  onAccepted,
}: TermsOfServiceModalProps) {
  const { t } = useLanguage();
  const userStore = useUserStore();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    const success = await userStore.acceptTos(userId);
    setSubmitting(false);
    if (success) {
      onAccepted?.();
    } else {
      toast.error(t.auth.tosFailedToAccept);
    }
  }, [userId, userStore, onAccepted, t]);

  // The checkbox label carries both links inline; split on the placeholders so
  // translators keep control of the sentence order.
  const labelParts = t.auth.tosCheckbox.split(/(\{terms\}|\{privacy\})/);

  return (
    <Dialog open modal>
      <DialogContent
        data-testid="tos-modal"
        className="max-w-lg"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">{t.auth.tosTitle}</DialogTitle>
          <DialogDescription>{t.auth.tosBody}</DialogDescription>
        </DialogHeader>

        <label className="flex cursor-pointer select-none items-start gap-2">
          <Checkbox
            className="mt-0.5"
            checked={accepted}
            onCheckedChange={(checked) => setAccepted(checked === true)}
          />
          <span className="text-sm">
            {labelParts.map((part, i) => {
              if (part === "{terms}") {
                return (
                  <a
                    key={i}
                    href={FRANKLIN_TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    // Without this the click bubbles to the label and toggles the box.
                    onClick={(e) => e.stopPropagation()}
                    className={LINK_CLASS}
                  >
                    {t.auth.tosTermsLink}
                  </a>
                );
              }
              if (part === "{privacy}") {
                return (
                  <a
                    key={i}
                    href={FRANKLIN_PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={LINK_CLASS}
                  >
                    {t.auth.tosPrivacyLink}
                  </a>
                );
              }
              return part;
            })}
          </span>
        </label>

        {/* The dialog cannot be dismissed, so this is the only way out for a user
            whose acceptance keeps failing — without it the toast is a dead end. */}
        <div className="flex items-center justify-between">
          <SignOutEscapeButton />
          <Button
            data-testid="tos-accept"
            onClick={handleAccept}
            disabled={!accepted || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.auth.tosAccepting}
              </>
            ) : (
              t.auth.tosAccept
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});
